import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FaChalkboardTeacher, FaChevronDown, FaChevronRight, FaChevronUp } from "react-icons/fa";
import GenderIcon from "../../components/GenderIcon";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../../components/lds/LdsTable";
import { childName, euro, openMonths, type ContributionOverview, type ContributionRow } from "./contributionsModel";
import { ContributionProgress, MonthStrip, OpenMonths } from "./ContributionDetails";
import { ChildContribution, type DetailAccess } from "./ChildView";
import type { FamilyOption } from "./FamilyPicker";
import { classLabel } from "./classLabel";

type TFn = (key: string, options?: Record<string, unknown>) => string;

// "Per class" view of the contributions page: the children of each class with their months and payments.

/** Key of the group for children without a class in the school year. */
export const NO_CLASS = "none";

export type ClassGroup = {
  key: string;
  name: string | null;
  grade: number | null;
  classType: string | null;
  teachers: string[];
  children: ContributionRow[];
};

/** "Kleuterklas", "Klas 3", or "No class" for the children without one. */
export const classGroupLabel = (t: TFn, group: ClassGroup): string => group.key === NO_CLASS
  ? t("finance.contributions.no_class", { defaultValue: "No class" })
  : classLabel(t, { className: group.name, classType: group.classType, classGrade: group.grade }) || "—";

const sum = (rows: ContributionRow[], pick: (row: ContributionRow) => number | string) =>
  rows.reduce((total, row) => total + Number(pick(row)), 0);

/** A child is behind when a month that was due is not (fully) paid. */
export const isBehind = (row: ContributionRow): boolean => openMonths(row.cells).length > 0;

/** The children grouped by class: lowest grade first, then by name; children without a class last. */
export function groupByClass(rows: ContributionRow[]): ClassGroup[] {
  const groups = new Map<string, ClassGroup>();
  rows.forEach((row) => {
    const key = row.classroomId || NO_CLASS;
    const placed = Boolean(row.classroomId);
    const group = groups.get(key) ?? {
      key,
      name: placed ? row.className ?? null : null,
      grade: placed ? row.classGrade ?? null : null,
      classType: placed ? row.classType ?? null : null,
      teachers: placed ? row.classTeachers ?? [] : [],
      children: [],
    };
    group.children.push(row);
    groups.set(key, group);
  });
  const byName = (a: ContributionRow, b: ContributionRow) =>
    String(a.lastName ?? "").localeCompare(String(b.lastName ?? ""), "nl")
    || String(a.firstName ?? "").localeCompare(String(b.firstName ?? ""), "nl");
  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      // Children still at school first, withdrawn children at the bottom of their class.
      children: [...group.children].sort((a, b) => Number(Boolean(a.withdrawnAt)) - Number(Boolean(b.withdrawnAt)) || byName(a, b)),
    }))
    .sort((a, b) => {
      if (a.key === NO_CLASS || b.key === NO_CLASS) return a.key === NO_CLASS ? 1 : -1;
      const kindergarten = (group: ClassGroup) => (String(group.classType ?? "").toUpperCase() === "KINDERGARTEN" ? 0 : 1);
      return kindergarten(a) - kindergarten(b)
        || (a.grade ?? Number.MAX_SAFE_INTEGER) - (b.grade ?? Number.MAX_SAFE_INTEGER)
        || String(a.name ?? "").localeCompare(String(b.name ?? ""), "nl", { numeric: true });
    });
}

const COLUMNS = 6;

export default function ClassView({
  groups, overview, detailAccess, canManage, expanded, onToggle, onChanged, familyOptions,
}: {
  groups: ClassGroup[];
  overview: ContributionOverview;
  detailAccess: DetailAccess;
  canManage: boolean;
  expanded: Set<string>;
  onToggle: (childId: string) => void;
  onChanged: () => void;
  familyOptions: FamilyOption[];
}): ReactElement {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggleClass = (key: string) => setCollapsed((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const families = new Map(overview.families.map((family) => [family.familyId, family]));

  return (
    <LdsTableScroll>
      <LdsTable className="w-full text-xs">
        <LdsTableHead>
          <LdsTableRow>
            <LdsTableHeadCell className="!px-3">{t("finance.contributions.columns.child", { defaultValue: "Child" })}</LdsTableHeadCell>
            <LdsTableHeadCell className="!px-3">{t("finance.contributions.columns.months", { defaultValue: "Months" })}</LdsTableHeadCell>
            <LdsTableHeadCell className="!px-3 text-right">{t("finance.contributions.columns.expected_to_date", { defaultValue: "Expected to date" })}</LdsTableHeadCell>
            <LdsTableHeadCell className="!px-3 text-right">{t("finance.contributions.columns.paid", { defaultValue: "Paid" })}</LdsTableHeadCell>
            <LdsTableHeadCell className="!px-3 text-right">{t("finance.contributions.columns.balance", { defaultValue: "Balance" })}</LdsTableHeadCell>
            <LdsTableHeadCell className="!px-2"><span className="sr-only">{t("finance.contributions.details", { defaultValue: "Details" })}</span></LdsTableHeadCell>
          </LdsTableRow>
        </LdsTableHead>
        <LdsTableBody>
          {groups.length > 0 ? groups.flatMap((group) => {
            const closed = collapsed.has(group.key);
            const name = classGroupLabel(t, group);
            const expected = sum(group.children, (row) => row.expectedToDate);
            const paid = sum(group.children, (row) => row.paid);
            const balance = sum(group.children, (row) => row.balance);
            const behind = group.children.filter(isBehind).length;
            const header = (
              <LdsTableRow key={`class-${group.key}`} data-class={group.key} onClick={() => toggleClass(group.key)}
                className="cursor-pointer bg-slate-100/80 hover:bg-slate-100 dark:bg-white/[0.06] dark:hover:bg-white/[0.08]">
                <LdsTableCell colSpan={2} className="!px-3 !py-2">
                  <button
                    type="button"
                    aria-expanded={!closed}
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleClass(group.key);
                    }}
                    className="inline-flex items-center gap-2 text-left text-sm font-bold text-slate-950 dark:text-white"
                  >
                    {closed ? <FaChevronRight aria-hidden="true" className="text-xs text-slate-500" /> : <FaChevronDown aria-hidden="true" className="text-xs text-slate-500" />}
                    {name}
                  </button>
                  <span className="ml-2 text-[11px] text-slate-500 dark:text-slate-400">
                    {t("finance.contributions.class_summary", {
                      count: group.children.length,
                      behind,
                      defaultValue: "{{count}} child(ren) · {{behind}} behind",
                    })}
                  </span>
                  {group.teachers.length > 0 && (
                    <span data-teachers="" className="mt-0.5 flex items-center gap-1.5 pl-5 text-[11px] text-slate-600 dark:text-slate-300">
                      <FaChalkboardTeacher aria-hidden="true" className="shrink-0 text-slate-400" />
                      <span className="sr-only">{t("finance.contributions.class_teachers", { defaultValue: "Teacher(s)" })}: </span>
                      {group.teachers.join(", ")}
                    </span>
                  )}
                </LdsTableCell>
                <LdsTableCell className="!px-3 !py-2 text-right font-semibold tabular-nums">{euro(expected)}</LdsTableCell>
                <LdsTableCell className="!px-3 !py-2 text-right font-semibold tabular-nums">{euro(paid)}</LdsTableCell>
                <LdsTableCell className="!px-3 !py-2 text-right">
                  <span className={`font-bold tabular-nums ${balance < 0 ? "text-rose-600 dark:text-rose-300" : balance > 0 ? "text-emerald-700 dark:text-emerald-300" : ""}`}>
                    {euro(balance)}
                  </span>
                </LdsTableCell>
                <LdsTableCell className="!px-2 !py-2" />
              </LdsTableRow>
            );
            if (closed) return [header];
            return [header, ...group.children.flatMap((row) => {
              const open = expanded.has(row.childId);
              const rowBalance = Number(row.balance);
              const summary = (
                <LdsTableRow
                  key={`${group.key}-${row.childId}`}
                  data-child={row.childId}
                  onClick={() => onToggle(row.childId)}
                  className={`cursor-pointer transition hover:bg-indigo-50/50 dark:hover:bg-white/[0.04] ${open ? "bg-indigo-50/40 dark:bg-white/[0.04]" : ""}`}
                >
                  <LdsTableCell className="!px-3 !py-2.5 align-top">
                    <span className="flex flex-wrap items-center gap-x-1.5 text-[13px] font-semibold text-slate-950 dark:text-white">
                      <GenderIcon gender={row.gender} className="shrink-0" />
                      {detailAccess.child ? (
                        <Link to={`/family/children/${row.childId}/details`} onClick={(event) => event.stopPropagation()}
                          className="hover:text-indigo-600 hover:underline dark:hover:text-indigo-300">
                          {childName(row)}
                        </Link>
                      ) : childName(row)}
                      {row.withdrawnAt && (
                        <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-600 dark:bg-white/10 dark:text-slate-300">
                          {t("finance.students_report.status.withdrawn", { defaultValue: "Withdrawn" })}
                        </span>
                      )}
                    </span>
                    <span className="block text-[11px] text-slate-500 dark:text-slate-400">
                      {row.familyId && detailAccess.family ? (
                        <Link to={`/family/${row.familyId}/details`} onClick={(event) => event.stopPropagation()}
                          className="hover:text-indigo-600 hover:underline dark:hover:text-indigo-300">
                          {row.familyName || "—"}
                        </Link>
                      ) : row.familyId ? (row.familyName || "—") : (
                        <span className="text-amber-700 dark:text-amber-300">
                          {t("finance.students_report.warnings.no_family", { defaultValue: "No family linked" })}
                        </span>
                      )}
                    </span>
                    <OpenMonths cells={row.cells} />
                  </LdsTableCell>
                  <LdsTableCell className="!px-3 !py-2.5 align-top">
                    <MonthStrip cells={row.cells} currentMonth={overview.currentMonth} />
                  </LdsTableCell>
                  <LdsTableCell className="!px-3 !py-2.5 text-right align-top tabular-nums">{euro(row.expectedToDate)}</LdsTableCell>
                  <LdsTableCell className="!px-3 !py-2.5 text-right align-top tabular-nums">{euro(row.paid)}</LdsTableCell>
                  <LdsTableCell className="!px-3 !py-2.5 text-right align-top">
                    <span className={`font-bold tabular-nums ${rowBalance < 0 ? "text-rose-600 dark:text-rose-300" : rowBalance > 0 ? "text-emerald-700 dark:text-emerald-300" : ""}`}>
                      {euro(row.balance)}
                    </span>
                    <span className="mt-1 flex justify-end"><ContributionProgress row={row} /></span>
                  </LdsTableCell>
                  <LdsTableCell className="!px-2 !py-2.5 text-right align-top">
                    <button
                      type="button"
                      aria-expanded={open}
                      aria-label={open
                        ? t("finance.students_report.hide_contribution", { name: childName(row), defaultValue: "Hide contribution of {{name}}" })
                        : t("finance.students_report.show_contribution", { name: childName(row), defaultValue: "Show contribution of {{name}}" })}
                      onClick={(event) => {
                        event.stopPropagation();
                        onToggle(row.childId);
                      }}
                      className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-white/10"
                    >
                      {open ? <FaChevronUp aria-hidden="true" /> : <FaChevronDown aria-hidden="true" />}
                    </button>
                  </LdsTableCell>
                </LdsTableRow>
              );
              if (!open) return [summary];
              return [
                summary,
                <LdsTableRow key={`${group.key}-${row.childId}-details`} data-details={row.childId} className="bg-indigo-50/40 dark:bg-white/[0.04]">
                  <LdsTableCell colSpan={COLUMNS} className="!px-4 !pb-4 !pt-1">
                    <ChildContribution
                      row={row}
                      siblings={row.familyId ? overview.rows.filter((other) => other.familyId === row.familyId) : []}
                      contributions={overview}
                      familyName={row.familyId ? families.get(row.familyId)?.familyName : undefined}
                      canManage={canManage}
                      onChanged={onChanged}
                      familyOptions={familyOptions}
                    />
                  </LdsTableCell>
                </LdsTableRow>,
              ];
            })];
          }) : (
            <LdsTableRow>
              <LdsTableCell colSpan={COLUMNS} className="py-8 text-center text-slate-500">
                {t("finance.students_report.empty", { defaultValue: "No children found." })}
              </LdsTableCell>
            </LdsTableRow>
          )}
        </LdsTableBody>
      </LdsTable>
    </LdsTableScroll>
  );
}
